/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";

const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // API URLs
  const PARTNERS_API_URL = `${process.env.REACT_APP_API_BASE_URL}/UIecosystemPartners`;
  const BANKS_API_URL = `${process.env.REACT_APP_API_BASE_URL}/UIbanksPartners`;
  const API_MEDIA = `${process.env.REACT_APP_API_BASE_URL}/UImedia`;

  // Fetch partners data with image loading
  const fetchPartners = async () => {
    try {
      const response = await axios.get(PARTNERS_API_URL);
      const data = response.data.results || response.data;

      // Load images for each partner
      const partnersWithImages = await Promise.all(
        data.map(async (partner) => {
          let logoUrl = "";
          
          // If logo is an object with id, download the image
          if (partner.logo?.id) {
            try {
              const imgRes = await axios.get(`${API_MEDIA}/${partner.logo.id}/download`, {
                responseType: "blob",
              });
              logoUrl = URL.createObjectURL(imgRes.data);
            } catch (error) {
              console.error("Error loading partner logo:", error);
              logoUrl = "https://via.placeholder.com/64?text=Logo";
            }
          } 
          // If logo is already a URL string
          else if (typeof partner.logo === 'string' && partner.logo.startsWith('http')) {
            logoUrl = partner.logo;
          }
          // Fallback
          else {
            logoUrl = "https://via.placeholder.com/64?text=Logo";
          }

          return {
            ...partner,
            logoUrl,
            website: partner.website || "#"
          };
        })
      );

      return partnersWithImages;
    } catch (error) {
      console.error("Error fetching partners:", error);
      throw error;
    }
  };

  // Fetch banks data with image loading
  const fetchBanks = async () => {
    try {
      const response = await axios.get(BANKS_API_URL);
      const data = response.data.results || response.data;

      // Load images for each bank
      const banksWithImages = await Promise.all(
        data.map(async (bank) => {
          let logoUrl = "";
          
          // If logo is an object with id, download the image
          if (bank.logo?.id) {
            try {
              const imgRes = await axios.get(`${API_MEDIA}/${bank.logo.id}/download`, {
                responseType: "blob",
              });
              logoUrl = URL.createObjectURL(imgRes.data);
            } catch (error) {
              console.error("Error loading bank logo:", error);
              logoUrl = "https://via.placeholder.com/64?text=Logo";
            }
          } 
          // If logo is already a URL string
          else if (typeof bank.logo === 'string' && bank.logo.startsWith('http')) {
            logoUrl = bank.logo;
          }
          // Fallback
          else {
            logoUrl = "https://via.placeholder.com/64?text=Logo";
          }

          return {
            ...bank,
            logoUrl,
            website: bank.website || "#"
          };
        })
      );

      return banksWithImages;
    } catch (error) {
      console.error("Error fetching banks:", error);
      throw error;
    }
  };

  // Fetch all data
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [partnersData, banksData] = await Promise.all([
          fetchPartners(),
          fetchBanks()
        ]);

        // Filter only active partners and banks if they have isActive field
        const activePartners = partnersData.filter(partner => partner.isActive !== false);
        const activeBanks = banksData.filter(bank => bank.isActive !== false);

        setPartners(activePartners);
        setBanks(activeBanks);
      } catch (err) {
        setError("Failed to load partners data. Please try again later.");
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      // Revoke object URLs to prevent memory leaks
      [...partners, ...banks].forEach(item => {
        if (item.logoUrl && item.logoUrl.startsWith('blob:')) {
          URL.revokeObjectURL(item.logoUrl);
        }
      });
    };
  }, [partners, banks]);

  // Gradient text style
  const gradientText = {
    background: "linear-gradient(90deg, #eab308, #ffffff, #eab308)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    color: "transparent",
  };

  // Animation variants for partner items
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
    hover: {
      y: -5,
      scale: 1.05,
      filter: "drop-shadow(0 0 10px rgba(234, 179, 8, 0.5))",
      transition: { duration: 0.3 },
    },
  };

  // Loading component
  if (loading) {
    return (
      <section className="bg-gray-900 py-16">
        <div className="container mx-auto text-center">
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
          </div>
          <p className="text-gray-300 mt-4">Loading partners...</p>
        </div>
      </section>
    );
  }

  // Error component
  if (error) {
    return (
      <section className="bg-gray-900 py-16">
        <div className="container mx-auto text-center">
          <p className="text-red-500 text-lg">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-6 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </section>
    );
  }

  return (
    <>
      {/* Ecosystem Partners Section */}
      <section className="bg-gray-900 py-16 relative overflow-hidden">
        {/* Subtle Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-900 to-black opacity-50 z-0" />

        <div className="container mx-auto text-center relative z-10">
          <motion.h2
            className="text-3xl md:text-4xl font-bold"
            style={gradientText}
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Our Ecosystem Partners
          </motion.h2>
          <p className="text-gray-300 mt-2 max-w-lg mx-auto">
            We proudly collaborate with industry leaders to deliver excellence.
          </p>

          {partners.length > 0 ? (
            <div className="flex flex-wrap justify-center gap-12 mt-12">
              {partners.map((partner) => (
                <motion.a
                  key={partner.id || partner._id}
                  href={partner.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center text-center w-48"
                  variants={itemVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  whileHover="hover"
                >
                  <img
                    src={partner.logoUrl}
                    alt={partner.name}
                    className="h-16 mx-auto object-contain"
                    onError={(e) => {
                      e.target.src = "https://via.placeholder.com/64?text=Logo";
                    }}
                  />
                  <p className="text-gray-200 font-semibold mt-4">{partner.name}</p>
                </motion.a>
              ))}
            </div>
          ) : (
            <div className="text-gray-400 mt-12">
              No ecosystem partners available at the moment.
            </div>
          )}
        </div>
      </section>

      {/* Bank & Exchange Partners Section */}
      <section className="bg-gray-900 py-16 relative overflow-hidden">
        {/* Subtle Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-900 to-black opacity-50 z-0" />

        <div className="container mx-auto text-center relative z-10">
          <motion.h2
            className="text-3xl md:text-4xl font-bold"
            style={gradientText}
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Bank & Exchange Partners
          </motion.h2>
          <p className="text-gray-300 mt-2 max-w-lg mx-auto">
            We proudly collaborate with industry leaders to deliver excellence.
          </p>

          {banks.length > 0 ? (
            <div className="flex flex-wrap justify-center gap-12 mt-12">
              {banks.map((bank) => (
                <motion.a
                  key={bank.id || bank._id}
                  href={bank.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center text-center w-48"
                  variants={itemVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  whileHover="hover"
                >
                  <img
                    src={bank.logoUrl}
                    alt={bank.name}
                    className="h-16 mx-auto object-contain"
                    onError={(e) => {
                      e.target.src = "https://via.placeholder.com/64?text=Logo";
                    }}
                  />
                  <p className="text-gray-200 font-semibold mt-4">{bank.name}</p>
                </motion.a>
              ))}
            </div>
          ) : (
            <div className="text-gray-400 mt-12">
              No bank partners available at the moment.
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Partners;